import LibraryAddOutlinedIcon from '@mui/icons-material/LibraryAddOutlined';
import { Alert, Box, Button, Snackbar, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAuth } from '@verantos-dev/core-auth';
import {
  Dialog,
  DialogButtons,
  DialogMessageBox,
  DialogTitle,
  Tooltip,
} from '@verantos-dev/shared-components';

import { useUpdateAvScopeMutation } from '../../api/useUpdateAvScopeMutation';
import type { VariableDetail } from '../../api/useVariableDetail';

interface AvScopeControlProps {
  detail: VariableDetail;
  /** Stable test-id namespace; defaults to the card's testid + ``-scope-control``. */
  'data-testid'?: string;
}

export const currentScope = (detail: VariableDetail): 'project' | 'organization' | null => {
  // Library rows carry SCOPE column on the wire; legacy rows can be null.
  // We treat null + "project" the same — the publish button shifts them
  // to "organization". Anything else (future scopes like "user") is
  // treated as unknown and the control hides itself.
  const raw = ((detail as { scope?: string | null }).scope ?? '').toLowerCase();
  if (raw === 'organization') return 'organization';
  if (raw === 'project' || raw === '') return 'project';
  return null;
};

/**
 * Publish (project -> organization) control for an AV's library scope.
 *
 * Carries forward the legacy CriteriaCard "Publish to library" UX pattern
 * (icon + label button) but acts on the existing ``ANALYTICAL_VARIABLE``
 * row via ``PATCH /analytical-variables/{id}`` rather than creating a
 * fresh row. One-way only — demoting back to project scope isn't
 * supported yet, so once ``scope === 'organization'`` the button stays
 * put but is permanently disabled (with an explanatory tooltip) rather
 * than being relabeled into an unsupported reverse action.
 *
 * "Share with organization" is disabled (with an explanatory tooltip)
 * whenever:
 *
 *   - the variable has already been shared (``scope === 'organization'``).
 *   - the variable's unified ``assetStatus`` isn't ``'Ready'`` — can't
 *     share an unverified/Draft variable.
 *   - the current user isn't the variable's creator/owner.
 *
 * Clicking the (enabled) button opens a confirm dialog — sharing widens
 * visibility org-wide and (per the promote-only model above) can never be
 * undone once another project starts using it, so the mutation only fires
 * after the user explicitly confirms. On success the same dialog swaps to a
 * success message and auto-closes after a 3-second countdown; on failure it
 * closes immediately and the error surfaces in the Snackbar below.
 */
export const AvScopeControl = ({
  detail,
  'data-testid': dataTestId = 'av-scope-control',
}: AvScopeControlProps) => {
  const { formatMessage } = useIntl();
  const scope = currentScope(detail);
  const mutation = useUpdateAvScopeMutation();
  const {
    user: { userId: currentUserId },
  } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  // Non-null while showing the post-share success message; counts down the
  // seconds left before the dialog auto-closes (mockup: "This window will
  // close automatically in 3 seconds.").
  const [successCountdown, setSuccessCountdown] = useState<number | null>(null);

  useEffect(() => {
    if (successCountdown === null) return;
    if (successCountdown <= 0) {
      setIsConfirmOpen(false);
      setSuccessCountdown(null);
      return;
    }
    const timer = setTimeout(() => setSuccessCountdown((n) => (n ?? 0) - 1), 1000);
    return () => clearTimeout(timer);
  }, [successCountdown]);

  // Also hides itself for unknown scopes (defensive — future scope values
  // shouldn't crash the page). Library / chat-side rows always know their
  // scope; the only ``null`` path is a partial fetch shape that predates
  // this control.
  if (scope === null) return null;

  const alreadyShared = scope === 'organization';

  // Creator-only gate (OR-6081 AC11). Only the user who originally
  // saved the variable can promote it. Other project members see the
  // button disabled with an explanatory tooltip. Server enforces the
  // same rule with a 403 — the FE disable is a UX courtesy so users
  // don't click into a guaranteed-denied state. ``detail.createdBy?.userId``
  // is the persisted creator id; when absent (legacy library rows
  // without audit columns), default to "not creator" — fail closed.
  const creatorUserId = detail.createdBy?.userId ?? null;
  const isCreator = creatorUserId !== null && creatorUserId === currentUserId;

  // A variable can only be shared once it's verified end-to-end — the same
  // unified Draft/Processing/Ready/Error vocabulary the header status pill
  // reads (``assetStatus``). The backend computes it unconditionally, so it is
  // safe to use here as a functional gate and not just a display value.
  const isReady = (detail.assetStatus ?? '').toLowerCase() === 'ready';
  // Static label — the button always reads "Share with organization" (never
  // "Return to project"); once shared, it's simply disabled rather than
  // relabeled into an unsupported reverse action.
  const label = formatMessage({ id: 'projects.analyticalVariables.scopeControl.shareWithOrg' });
  const tooltip = alreadyShared
    ? formatMessage({ id: 'projects.analyticalVariables.scopeControl.tooltip.alreadyShared' })
    : !isReady
    ? formatMessage({ id: 'projects.analyticalVariables.scopeControl.tooltip.notReady' })
    : isCreator
    ? formatMessage({ id: 'projects.analyticalVariables.scopeControl.tooltip.shareWithOrg' })
    : formatMessage({ id: 'projects.analyticalVariables.scopeControl.tooltip.notCreator' });

  const displayName = detail.dataElement ?? detail.name;

  const closeDialog = () => {
    setIsConfirmOpen(false);
    setSuccessCountdown(null);
  };

  const onConfirmShare = async () => {
    setErrorMessage(null);
    try {
      await mutation.mutateAsync({
        analyticalVariableId: detail.analyticalVariableId,
        scope: 'organization',
      });
      setSuccessCountdown(3);
    } catch (err) {
      closeDialog();
      setErrorMessage(
        err instanceof Error
          ? err.message
          : formatMessage({ id: 'projects.analyticalVariables.scopeControl.error.generic' })
      );
    }
  };

  return (
    <Box data-testid={dataTestId}>
      <Tooltip id={dataTestId} title={tooltip} placement="left" arrow>
        <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={<LibraryAddOutlinedIcon />}
            onClick={() => setIsConfirmOpen(true)}
            disabled={mutation.isPending || !isCreator || !isReady || alreadyShared}
            data-testid={`${dataTestId}-action`}
            sx={{ textTransform: 'none' }}
          >
            {mutation.isPending
              ? formatMessage({ id: 'projects.analyticalVariables.scopeControl.updating' })
              : label}
          </Button>
        </span>
      </Tooltip>
      {isConfirmOpen && (
        <Dialog
          id={`${dataTestId}-confirm-dialog`}
          open
          PaperProps={{ sx: { width: '480px', maxHeight: '80%', margin: 0 } }}
          onClose={closeDialog}
        >
          <DialogTitle
            id={`${dataTestId}-confirm-dialog-title`}
            showClose
            onClose={closeDialog}
            disabled={mutation.isPending}
          >
            {formatMessage({ id: 'projects.analyticalVariables.scopeControl.confirmDialog.title' })}
          </DialogTitle>
          {/* DialogButtons normally supplies the dialog's bottom padding via
              its DialogActions root; the success state renders no buttons,
              so this Box adds that padding back itself to avoid a
              flush/cut-off bottom edge. */}
          <Box sx={{ px: 6, pb: successCountdown === null ? 0 : 6 }}>
            <DialogMessageBox
              id={`${dataTestId}-confirm-dialog-message`}
              type={successCountdown === null ? 'info' : 'success'}
              title={formatMessage({
                id:
                  successCountdown === null
                    ? 'projects.analyticalVariables.scopeControl.confirmDialog.messageTitle'
                    : 'projects.analyticalVariables.scopeControl.confirmDialog.successTitle',
              })}
              text={formatMessage(
                {
                  id:
                    successCountdown === null
                      ? 'projects.analyticalVariables.scopeControl.confirmDialog.messageText'
                      : 'projects.analyticalVariables.scopeControl.confirmDialog.successText',
                },
                { name: displayName }
              )}
            />
            {successCountdown !== null && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1 }}
                data-testid={`${dataTestId}-confirm-dialog-countdown`}
              >
                {formatMessage(
                  {
                    id: 'projects.analyticalVariables.scopeControl.confirmDialog.autoCloseCountdown',
                  },
                  { seconds: successCountdown }
                )}
              </Typography>
            )}
          </Box>
          {successCountdown === null && (
            <DialogButtons
              id={`${dataTestId}-confirm-dialog`}
              cancelButton={{
                label: formatMessage({
                  id: 'projects.analyticalVariables.scopeControl.confirmDialog.cancelButton',
                }),
                disabled: mutation.isPending,
                onClick: closeDialog,
              }}
              saveButton={{
                label: formatMessage({
                  id: 'projects.analyticalVariables.scopeControl.confirmDialog.confirmButton',
                }),
                color: 'primary',
                wait: mutation.isPending,
                onClick: onConfirmShare,
              }}
            />
          )}
        </Dialog>
      )}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          onClose={() => setErrorMessage(null)}
          data-testid={`${dataTestId}-error`}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
